import type { Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { beamSolid, hexahedronSolid } from './solids';

/*
 * The fixed luifels over the shop fronts of the Warmoesstraat between the Sint Olofspoort and the
 * Guldehandsteeg, where merchants met under the awnings (Hell 2017; docs/plans/houses.md, "Luifels").
 * No source gives a luifel's size; the height, slope, reach and braces are estimates within the one
 * rule found: its lowest edge 8 voet (2.26 m) or more over the street (keur of about 1413, Breen pp. 14-15).
 */
export const LUIFEL = {
  hinge: 3.2,
  /** Where the roof or the gable's coping begins, a luifel keeps this far below it. */
  belowEaves: 0.2,
  slope: (20 * Math.PI) / 180,
  reach: 1.3,
  board: 0.05,
  clearance: 2.26,
  /** Kept off the lot's side lines, so neighbouring luifels never share a face. */
  inset: 0.02,
  brace: { from: 0.15, drop: 0.8, out: 0.8, half: 0.04 },
  /** The luifel roof on the painting's window row, L* 23.4 (docs/reference/crops/window-row.png). */
  colour: '#48340c',
} as const;

/** The front a luifel hangs on, in the house's frame: x back from the lot's front line, y up, z across. */
export interface LuifelWall {
  /** Where the front stands at a height: a timber front's storeys stand each over the one below. */
  readonly frontAt: (y: number) => number;
  readonly half: number;
  readonly eaves: number;
}

export interface LuifelParts {
  /** The board's corners as hexahedronSolid takes them: bit 0 across, bit 1 up, bit 2 out. */
  readonly board: readonly Vec3[];
  /** Each brace's foot on the wall and head under the board. */
  readonly braces: readonly (readonly [Vec3, Vec3])[];
}

export function luifelParts(wall: LuifelWall): LuifelParts {
  const { slope, board: thick, clearance, inset, brace } = LUIFEL;
  const tan = Math.tan(slope);
  const hinge = Math.min(LUIFEL.hinge, wall.eaves - LUIFEL.belowEaves);
  const reach = Math.min(LUIFEL.reach, (hinge - thick - clearance) / tan);
  const x = wall.frontAt(hinge);
  const board = Array.from({ length: 8 }, (_, i): Vec3 => {
    const [across, up, out] = [i & 1, (i >> 1) & 1, (i >> 2) & 1];
    const z = across ? wall.half - inset : -wall.half + inset;
    return [x - out * reach, hinge - out * reach * tan - (up ? 0 : thick), z];
  });
  /* A brace's head sits a quarter of its half-width into the board: no gap shows, and its corners stay inside. */
  const headY = hinge - thick - brace.out * tan + brace.half / 4;
  const footY = hinge - brace.drop;
  const braces = [-1, 1].map((side): readonly [Vec3, Vec3] => {
    const z = side * (wall.half - brace.from);
    return [
      [wall.frontAt(footY), footY, z],
      [x - brace.out, headY, z],
    ];
  });
  return { board, braces };
}

export function buildLuifel(builder: MeshBuilder, style: VertexStyle, frame: Frame, wall: LuifelWall): void {
  const { board, braces } = luifelParts(wall);
  hexahedronSolid(builder, style, frame, board);
  for (const [foot, head] of braces) beamSolid(builder, style, frame, foot, head, LUIFEL.brace.half);
}
